import {
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Signal,
} from '@angular/core';

import { DownloadChartsPdfDialogComponent } from '../components/download-charts-pdf/download-charts-pdf-dialog/download-charts-pdf-dialog.component';
import type {
  CreateDialogArgs,
  DialogApi,
} from '../types/download-charts-pdf-dialog.types';
import type { KpiChart } from '../types/download-charts-pdf.types';
import { ChartsService } from './bar-charts-service';

@Injectable({ providedIn: 'root' })
export class DownloadChartsPdfDialogService {
  private readonly appRef = inject(ApplicationRef);

  private readonly envInjector = inject(EnvironmentInjector);

  openDialog(args: CreateDialogArgs): DialogApi {
    const containerId = `ops-container-${crypto.randomUUID()}`;
    const displayCharts = this.extractChartsMatrix(args.chartsService);

    // Create a hidden wrapper attached to the body — invisible to users
    // but fully rendered with concrete dimensions for html2canvas.
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '0';
    wrapper.style.top = '0';
    wrapper.style.width = '1200px';
    wrapper.style.overflow = 'auto';
    wrapper.style.maxHeight = '100vh';
    wrapper.style.zIndex = '-9999';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.opacity = '0.001';
    wrapper.style.background = '#f8f8f8';
    document.body.appendChild(wrapper);

    const componentRef = createComponent(DownloadChartsPdfDialogComponent, {
      environmentInjector: this.envInjector,
      hostElement: wrapper,
    });

    componentRef.instance.containerId = containerId;
    componentRef.instance.displayCharts = displayCharts;

    this.appRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();

    return {
      dispose: () => {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
        wrapper.remove();
      },
      get htmlElement() {
        return document.getElementById(containerId);
      },
    };
  }

  private extractChartsMatrix(
    chartsService: ChartsService,
  ): Signal<KpiChart>[][] {
    return [
      [
        chartsService.mttrAllNOCCasesLife.signal,
        chartsService.yearNitelNps.signal,
        chartsService.csatSurveyResults.signal,
      ],
      [
        chartsService.activeServices.signal,
        chartsService.inflightOrders.signal,
        chartsService.newClosedCases.signal,
      ],
      [
        chartsService.closedCases.signal,
        chartsService.oldOpenCareCases.signal,
        chartsService.openCareCases.signal,
      ],
      [
        chartsService.oldOpenNOCCases.signal,
        chartsService.openNOCCases.signal,
        chartsService.oldOpenSupportCases.signal,
      ],
      [
        chartsService.openSuportCases.signal,
        chartsService.oldOpenFinanceCases.signal,
        chartsService.openFinanceCases.signal,
      ],
      [
        chartsService.emergencyCases.signal,
        chartsService.openMaintenanceCases.signal,
      ],
      [chartsService.caseHistoryByDate.signal],
      // MN1-2222: mttrCircuitDown chart is excluded from the PDF export to
      // stay consistent with it being hidden on the ticket insights page.
      [chartsService.ytdNocCasesClosedCode.signal],
    ];
  }
}
